import { Button, darken, IconButton, lighten, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ClassNameMap } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import clsx from "clsx";
// eslint-disable-next-line no-restricted-imports
import { SnackbarContent } from "notistack";
import { forwardRef, ReactNode, useCallback, useMemo } from "react";
import { UIAction } from "../types/uiActions";
import { AsyncButton } from "./forms/AsyncButton";
import { UIActionButton } from "./UIActionButton";

const useStyles = makeStyles(
  (theme) => ({
    root: {
      borderRadius: 4,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      maxWidth: 530,
      minWidth: 100,
      padding: theme.spacing(1.25, 1.5, 1),
      "&$dismissable": {
        paddingRight: theme.spacing(5),
      },
    },
    close: {
      position: "absolute",
      top: theme.spacing(0.25),
      right: theme.spacing(0.5),
      padding: theme.spacing(1),
    },
    closeIcon: {
      width: 22,
      height: 22,
    },
    content: {
      ...theme.typography.body2,
      "& a": {
        color: theme.palette.primary.light,
      },
    },
    dismissContentPadding: {
      paddingRight: theme.spacing(6),
    },
    defaultColors: {
      backgroundColor: theme.colors.black,
      color: theme.colors.white,
    },
    errorColors: {
      backgroundColor: lighten(theme.palette.error.light, 0.8),
      color: theme.palette.error.dark,
    },
    warningColors: {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.dark,
    },
    successColors: {
      backgroundColor: lighten(theme.palette.success.light, 0.7),
      color: darken(theme.palette.success.dark, 0.3),
    },
    actionsWrap: {},
    dismissable: {},
    actionBtn: {
      paddingLeft: 0,
    },
    message: {
      whiteSpace: "pre-line",
    },
  }),
  {
    classNamePrefix: "Snackbar",
  }
);

export type SnackbarPropFactoryHelpers = Readonly<{
  readonly close: () => void;
}>;

export type SnackbarPropFactory<T> = T | ((helpers: SnackbarPropFactoryHelpers) => T);

export type SnackbarJSSClassKey = keyof ReturnType<typeof useStyles>;

export type SnackbarType = "default" | "error" | "warning" | "success";

export type SnackbarOptionsProps = {
  type?: SnackbarType;
  header?: SnackbarPropFactory<ReactNode>;
  message: SnackbarPropFactory<ReactNode>;
  uiActions?: SnackbarPropFactory<UIAction[]>;
  dismissible?: boolean;
  onView?: () => void;
  onUndo?: () => Promise<void> | void;
};

export type SnackbarProps = SnackbarOptionsProps & {
  classes?: Partial<ClassNameMap<SnackbarJSSClassKey>>;
  className?: string;
  onClose(): void;
};

function useResolvedFactory<T>(factory: SnackbarPropFactory<T>, helpers: SnackbarPropFactoryHelpers): T {
  return useMemo(() => {
    if (typeof factory === "function") return (factory as (helpers: SnackbarPropFactoryHelpers) => T)(helpers);
    else return factory;
  }, [factory, helpers]);
}

export const Snackbar = forwardRef<HTMLDivElement, SnackbarProps>(
  (
    {
      className,
      classes: extClasses,
      onClose,
      type = "default",
      message,
      dismissible,
      onUndo,
      onView,
      header,
      uiActions,
    },
    ref
  ) => {
    const classes = useStyles({
      classes: extClasses,
    });

    const helpers = useMemo<SnackbarPropFactoryHelpers>(() => Object.freeze({ close: onClose }), [onClose]);

    header = useResolvedFactory(header, helpers);
    message = useResolvedFactory(message, helpers);
    uiActions = useResolvedFactory(uiActions, helpers);

    const colorClassMap = {
      [classes.defaultColors]: type === "default",
      [classes.errorColors]: type === "error",
      [classes.warningColors]: type === "warning",
      [classes.successColors]: type === "success",
    };

    const handleUndoClick = useCallback(async () => {
      await onUndo?.();
      await onClose();
    }, [onUndo, onClose]);

    return (
      <SnackbarContent
        ref={ref}
        className={clsx(classes.root, className, colorClassMap, { [classes.dismissable]: !!dismissible })}
      >
        {!!dismissible && (
          <IconButton aria-label="close" className={classes.close} onClick={onClose} size="large">
            <CloseIcon className={clsx(classes.closeIcon, colorClassMap)} />
          </IconButton>
        )}

        <div className={clsx(classes.content, { [classes.dismissContentPadding]: !!dismissible })}>
          {header ?? <Typography variant="h3">{header}</Typography>}
          <div className={classes.message}>{message}</div>
        </div>

        <div className={classes.actionsWrap}>
          {!!onUndo && (
            <AsyncButton
              className={clsx(classes.actionBtn, colorClassMap)}
              onClick={handleUndoClick}
              size="small"
              color="primary"
            >
              Undo
            </AsyncButton>
          )}

          {!!onView && (
            <Button className={clsx(classes.actionBtn, colorClassMap)} onClick={onUndo} size="small" color="primary">
              View
            </Button>
          )}

          {(uiActions || []).map((uiAction, i) => (
            <UIActionButton key={i} className={clsx(classes.actionBtn, colorClassMap)} uiAction={uiAction} />
          ))}
        </div>
      </SnackbarContent>
    );
  }
);
